/**
 * Runner policy layers — hooks for global / profiles / runner overrides.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

export type ApprovalMode = 'explicit_approval' | 'default' | 'bypass';
export type ApprovalScope = 'all' | 'outside_allowed_paths';

export interface RunnerPolicyConfig {
  mode?: ApprovalMode;
  approval_required_for?: string[];
  approval_required_patterns?: string[];
  approval_exceptions?: string[];
  approval_scope?: ApprovalScope;
  require_approval_actions?: string[];
  blocked_tool_names?: string[];
  blocked_commands?: string[];
  allowed_commands?: string[];
  blocked_patterns?: string[];
  no_sudo?: boolean;
  no_interactive?: boolean;
  no_pipe_to_shell?: boolean;
  no_background?: boolean;
  allowed_paths?: string[];
  blocked_paths?: string[];
  max_timeout_seconds?: number;
  max_output_size?: number;
  rate_limit_per_minute?: number;
  rate_limit_per_hour?: number;
}

export interface PolicyProfile {
  id: string;
  tenant_id: string;
  name: string | null;
  config: RunnerPolicyConfig;
  runner_ids: string[];
}

export interface RunnerPolicyEffective {
  runner_id: string;
  profile_id: string | null;
  profile_name: string | null;
  config: RunnerPolicyConfig;
}

export const policyKeys = {
  all: (tenantId: string) => ['runner-policy', tenantId] as const,
  global: (tenantId: string) => ['runner-policy', tenantId, 'global'] as const,
  profiles: (tenantId: string) => ['runner-policy', tenantId, 'profiles'] as const,
  runnerEffective: (tenantId: string, runnerId: string) =>
    ['runner-policy', tenantId, 'effective', runnerId] as const,
};

const base = (tenantId: string) => `/api/v1/tenants/${tenantId}/mcp/policy`;

export function useGlobalPolicy() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? '';
  return useQuery({
    queryKey: policyKeys.global(tenantId),
    queryFn: async () => {
      const { data } = await apiClient.get<{ config: RunnerPolicyConfig }>(
        `${base(tenantId)}/global`,
      );
      return data.config;
    },
    enabled: !!tenantId,
  });
}

export function usePolicyProfiles() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? '';
  return useQuery({
    queryKey: policyKeys.profiles(tenantId),
    queryFn: async () => {
      const { data } = await apiClient.get<PolicyProfile[]>(`${base(tenantId)}/profiles`);
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useRunnerEffectivePolicy(runnerId: string | null) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? '';
  return useQuery({
    queryKey: policyKeys.runnerEffective(tenantId, runnerId ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<RunnerPolicyEffective>(
        `${base(tenantId)}/runners/${runnerId}/effective`,
      );
      return data;
    },
    enabled: !!tenantId && !!runnerId,
  });
}

export function useSaveGlobalPolicy() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (config: RunnerPolicyConfig) => {
      const { data } = await apiClient.put(`${base(tenantId)}/global`, config);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: policyKeys.all(tenantId) });
    },
  });
}

export function useSaveProfile() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      name: string | null;
      config: RunnerPolicyConfig;
      runner_ids?: string[];
    }) => {
      if (input.id) {
        const { data } = await apiClient.put(`${base(tenantId)}/profiles/${input.id}`, input);
        return data;
      }
      const { data } = await apiClient.post(`${base(tenantId)}/profiles`, input);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: policyKeys.all(tenantId) });
    },
  });
}

export function useSaveRunnerOverrides() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { runnerId: string; config: RunnerPolicyConfig }) => {
      const { data } = await apiClient.put(
        `${base(tenantId)}/runners/${input.runnerId}`,
        input.config,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: policyKeys.all(tenantId) });
    },
  });
}
